import { createContext, useState } from 'react';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBarEntry } from '@nucleus-commerce/admin-kit/save-bar';
import type { MarketPrice } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import type { Money } from '@nucleus-commerce/core';

import { refusedVariantId } from '../schemas/market-price-rules';

import { useRemoveMarketPrice, useSetMarketPrices } from './use-market-prices';

interface MarketCellDraft {
  marketId: string;
  variantId: string;
  /** Null clears the fixed price: the store price, converted, applies again. */
  price: Money | null;
  compareAt: Money | null;
}

const cellKey = (marketId: string, variantId: string) => `${marketId}:${variantId}`;

export interface MarketPriceDrafts {
  /** What the input shows: the draft, or the saved fixed price. */
  priceFor: (marketId: string, variantId: string, entry: MarketPrice | undefined) => Money | null;
  setPrice: (
    marketId: string,
    variantId: string,
    price: Money | null,
    entry: MarketPrice | undefined,
  ) => void;
  isDirty: (marketId: string, variantId: string) => boolean;
  errorFor: (marketId: string, variantId: string) => string | undefined;
  /** Why a failed save refused a variant, to open its row. */
  failures: ReadonlyMap<string, string>;
  canWrite: boolean;
}

/** Lets a store variant's View more reach the market price drafts. */
export const MarketPriceDraftsContext = createContext<MarketPriceDrafts | null>(null);

/**
 * Fixed market prices typed in a store variant's View more: drafts with their own save bar entry,
 * written next to the store prices. A blank input removes the fixed price.
 */
export function useMarketPriceDrafts(
  storeId: string,
  storeProductId: string,
  productId: string,
): MarketPriceDrafts {
  const { can } = usePermissions();
  const canWrite = can('products.write');
  const message = useErrorMessage();
  const set = useSetMarketPrices(storeId, storeProductId, productId);
  const remove = useRemoveMarketPrice(storeId, storeProductId, productId);
  const [drafts, setDrafts] = useState<ReadonlyMap<string, MarketCellDraft>>(new Map());
  const [errors, setErrors] = useState<ReadonlyMap<string, { variantId: string; text: string }>>(
    new Map(),
  );

  const forget = (cells: readonly MarketCellDraft[]) =>
    setDrafts((current) => {
      const next = new Map(current);
      for (const cell of cells) next.delete(cellKey(cell.marketId, cell.variantId));
      return next;
    });

  const save = async () => {
    const refused = new Map<string, { variantId: string; text: string }>();
    const refuse = (marketId: string, variantId: string, text: string) =>
      refused.set(cellKey(marketId, variantId), { variantId, text });
    const byMarket = new Map<string, MarketCellDraft[]>();
    for (const cell of drafts.values()) {
      byMarket.set(cell.marketId, [...(byMarket.get(cell.marketId) ?? []), cell]);
    }
    await Promise.all(
      [...byMarket].map(async ([marketId, cells]) => {
        const fixing = cells.flatMap((cell) =>
          cell.price
            ? [{ variantId: cell.variantId, price: cell.price, compareAt: cell.compareAt }]
            : [],
        );
        if (fixing.length > 0) {
          try {
            await set.mutateAsync({ marketId, body: { items: fixing } });
            forget(cells.filter((cell) => cell.price));
          } catch (error) {
            const details = isApiError(error) ? error.details : [];
            let placed = false;
            for (const detail of details) {
              const variantId = refusedVariantId(detail.field, fixing);
              if (!variantId) continue;
              refuse(marketId, variantId, detail.message);
              placed = true;
            }
            if (!placed) {
              for (const item of fixing) refuse(marketId, item.variantId, message(error));
            }
          }
        }
        for (const cell of cells.filter((candidate) => !candidate.price)) {
          try {
            await remove.mutateAsync({ marketId, variantId: cell.variantId });
            forget([cell]);
          } catch (error) {
            refuse(marketId, cell.variantId, message(error));
          }
        }
      }),
    );
    setErrors(refused);
  };

  useSaveBarEntry({
    dirty: drafts.size > 0,
    saving: set.isPending || remove.isPending,
    onSave: save,
    onDiscard: () => {
      setDrafts(new Map());
      setErrors(new Map());
    },
    disabled: !canWrite,
  });

  return {
    priceFor: (marketId, variantId, entry) => {
      const draft = drafts.get(cellKey(marketId, variantId));
      return draft ? draft.price : (entry?.price ?? null);
    },
    setPrice: (marketId, variantId, price, entry) => {
      const key = cellKey(marketId, variantId);
      const same = (price?.amount ?? null) === (entry?.price.amount ?? null);
      setDrafts((current) => {
        const next = new Map(current);
        if (same) next.delete(key);
        else {
          next.set(key, {
            marketId,
            variantId,
            price,
            compareAt: entry?.compareAt ?? null,
          });
        }
        return next;
      });
      setErrors((current) => {
        if (!current.has(key)) return current;
        const next = new Map(current);
        next.delete(key);
        return next;
      });
    },
    isDirty: (marketId, variantId) => drafts.has(cellKey(marketId, variantId)),
    errorFor: (marketId, variantId) => errors.get(cellKey(marketId, variantId))?.text,
    failures: new Map([...errors.values()].map((error) => [error.variantId, error.text])),
    canWrite,
  };
}
